import { Suspense, useState } from "react";
import { useRouteContext } from "@tanstack/react-router";
import { Input, type InputChangeEvent } from "@progress/kendo-react-inputs";
import type { GridRowClickEvent } from "@progress/kendo-react-grid";
import { TbUserSearch } from "react-icons/tb";
import Card from "@/components/shared/Card";
import Popup from "@/components/shared/Popup";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import SpinbuttonDateInput from "@/components/shared/forms/SpinbuttonDateInput";
import { fetchAuthenticatedData } from "@/queries/shared";
import { useChildEntities } from "@/queries";
import type { SearchResult, GlobalStateModel } from "@/models";
import type { CHLD } from "@/models/chld";
import { formatDateOnly } from "@/utils/dates";
import { getGenderText } from "./childDataViewTransformers";
import { ChildDataDetail } from "./ChildDataDetail";

type SearchFormState = {
  csppid: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
};

const initialForm: SearchFormState = {
  csppid: "",
  firstName: "",
  lastName: "",
  dateOfBirth: "",
};

type ResultRow = {
  CSPPID: string;
  ChildName: string;
  ChildDateOfBirth: string;
  ChildGender: string;
  ReportingLEA: string;
  Score: number | null;
};

const columns = [
  { field: "CSPPID", title: "CSPPID", width: "140px" },
  { field: "ChildName", title: "Child Name", width: "200px" },
  { field: "ChildDateOfBirth", title: "Birth Date", width: "120px" },
  { field: "ChildGender", title: "Gender", width: "100px" },
  { field: "ReportingLEA", title: "LEA", width: "160px" },
];

export function ChildDataView() {
  const leaContext = useRouteContext({ from: "/$leaid" }) as GlobalStateModel;
  const leaList = useChildEntities("0-").data;

  const [formState, setFormState] = useState<SearchFormState>(initialForm);
  const [dobResetKey, setDobResetKey] = useState(0);
  const [dobIncomplete, setDobIncomplete] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [showNoResults, setShowNoResults] = useState(false);
  const [popupResults, setPopupResults] = useState<ResultRow[]>([]);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedCsppid, setSelectedCsppid] = useState<string | undefined>();

  const handleChange = (key: keyof SearchFormState, value: string) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
    if (validationError) setValidationError("");
    if (searchError) setSearchError("");
  };

  const validate = (): boolean => {
    const hasCSPPID = formState.csppid.trim().length > 0;
    const hasFirstName = formState.firstName.trim().length > 0;
    const hasLastName = formState.lastName.trim().length > 0;
    const hasDOB = formState.dateOfBirth.trim().length === 10;

    if (formState.dateOfBirth && formState.dateOfBirth < "1900-01-01") {
      setValidationError("Please enter a valid Date of Birth.");
      return false;
    }

    if (hasCSPPID) return true;

    if (hasFirstName && hasLastName && hasDOB) return true;

    if (
      hasFirstName ||
      hasLastName ||
      formState.dateOfBirth.trim().length > 0 ||
      dobIncomplete
    ) {
      setValidationError(
        dobIncomplete
          ? "Please enter a complete Date of Birth."
          : "Please enter First Name, Last Name, and Date of Birth together."
      );
      return false;
    }

    setValidationError(
      "Please enter either CSPPID or First Name, Last Name, and Date of Birth."
    );
    return false;
  };

  const handleSearch = async () => {
    if (!validate()) return;

    setIsLoading(true);
    setSearchError("");
    setHasSearched(true);
    setShowNoResults(false);

    try {
      const results = await fetchAuthenticatedData<SearchResult<CHLD>[]>(
        leaContext,
        "/json/child/search",
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            CSPPID: formState.csppid,
            ChildFirstName: formState.firstName,
            ChildLastName: formState.lastName,
            ChildDateOfBirth: formState.dateOfBirth || "2026-01-01",
          }),
        }
      );

      if (results.length === 0) {
        setShowNoResults(true);
        setSelectedCsppid(undefined);
      } else if (results.length === 1) {
        setSelectedCsppid(results[0].matchRecord.CSPPID);
      } else {
        setPopupResults(
          results.map((r) => ({
            CSPPID: r.matchRecord.CSPPID || "",
            ChildName: [
              r.matchRecord.ChildFirstName,
              r.matchRecord.ChildMiddleName,
              r.matchRecord.ChildLastName,
            ]
              .filter(Boolean)
              .join(" "),
            ChildDateOfBirth: r.matchRecord.ChildDateOfBirth
              ? formatDateOnly(r.matchRecord.ChildDateOfBirth)
              : "",
            ChildGender: getGenderText(r.matchRecord.ChildGender),
            ReportingLEA:
              leaList?.find((x) => x.CDSCode === r.matchRecord.ReportingLEA)
                ?.LEAName || r.matchRecord.ReportingLEA || "",
            Score: r.score ?? null,
          }))
        );
        setShowPopup(true);
      }
    } catch (error) {
      setSearchError(
        error instanceof Error
          ? error.message
          : "An error occurred. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setFormState(initialForm);
    setDobResetKey((k) => k + 1);
    setDobIncomplete(false);
    setValidationError("");
    setSearchError("");
    setHasSearched(false);
    setShowNoResults(false);
    setSelectedCsppid(undefined);
  };

  const handleRowClick = (event: GridRowClickEvent) => {
    const row = event.dataItem as ResultRow;
    setSelectedCsppid(row.CSPPID);
    setShowPopup(false);
  };

  return (
    <>
      <Card className="card-custom mb-5 shadow-none">
        <div className="mb-0 p-4 bg-white w-100">
          <div className="row g-3">
            <div className="col-12 col-md-3">
              <label
                id="child-data-view-csppid-label"
                htmlFor="child-data-view-csppid"
                className="form-label fw-semibold"
              >
                CSPPID
              </label>
              <Input
                id="child-data-view-csppid"
                ariaLabelledBy="child-data-view-csppid-label"
                style={{ width: "100%" }}
                value={formState.csppid}
                onChange={(e: InputChangeEvent) =>
                  handleChange("csppid", e.value)
                }
                placeholder="Enter CSPPID"
                disabled={isLoading}
              />
            </div>

            <div className="col-12 col-md-3">
              <label
                id="child-data-view-first-name-label"
                htmlFor="child-data-view-first-name"
                className="form-label fw-semibold"
              >
                First Name
              </label>
              <Input
                id="child-data-view-first-name"
                ariaLabelledBy="child-data-view-first-name-label"
                style={{ width: "100%" }}
                value={formState.firstName}
                onChange={(e: InputChangeEvent) =>
                  handleChange("firstName", e.value)
                }
                placeholder="Enter first name"
                disabled={isLoading}
              />
            </div>

            <div className="col-12 col-md-3">
              <label
                id="child-data-view-last-name-label"
                htmlFor="child-data-view-last-name"
                className="form-label fw-semibold"
              >
                Last Name
              </label>
              <Input
                id="child-data-view-last-name"
                ariaLabelledBy="child-data-view-last-name-label"
                style={{ width: "100%" }}
                value={formState.lastName}
                onChange={(e: InputChangeEvent) =>
                  handleChange("lastName", e.value)
                }
                placeholder="Enter last name"
                disabled={isLoading}
              />
            </div>

            <div className="col-12 col-md-3">
              <label
                id="child-data-view-dob-label"
                className="form-label fw-semibold"
              >
                Birthdate
                <span className="visually-hidden"> format mm/dd/yyyy</span>
              </label>
              <SpinbuttonDateInput
                key={dobResetKey}
                id="child-data-view-dob"
                labelledBy="child-data-view-dob-label"
                style={{ width: "100%" }}
                value={formState.dateOfBirth}
                onChange={(value) => handleChange("dateOfBirth", value)}
                onIncompleteChange={setDobIncomplete}
                disabled={isLoading}
                max="9999-12-31"
              />
            </div>

            {(validationError || searchError) && (
              <div className="col-12">
                <div
                  className="alert mb-0"
                  style={{
                    background: validationError ? "#fff3cd" : "#f8d7da",
                    border: `1px solid ${validationError ? "#ffc107" : "#f5c2c7"}`,
                    color: validationError ? "#856404" : "#842029",
                    borderRadius: "4px",
                    fontSize: "14px",
                    padding: "12px 16px",
                  }}
                >
                  {validationError || searchError}
                </div>
              </div>
            )}

            <div className="col-12 d-flex align-items-end gap-2 mt-4 mb-1">
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={() => void handleSearch()}
                disabled={isLoading}
              >
                <i className="bi bi-search" aria-hidden="true" />
                {isLoading ? "Searching..." : "Search"}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleClear}
                disabled={isLoading}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </Card>

      {!hasSearched && !selectedCsppid ? (
        <Card className="card-custom shadow-none">
          <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                backgroundColor: "#e9ecef",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "1rem",
              }}
            >
              <TbUserSearch size={30} color="#6c757d" />
            </div>
            <h5 className="fw-semibold mb-2" style={{ color: "#0d2d4a" }}>
              Search for a Child
            </h5>
            <p
              className="mb-0"
              style={{ maxWidth: 380, fontSize: "0.9rem", color: "#212529" }}
            >
              Enter a CSPPID, or a First Name, Last Name, and Birthdate
              together, then click Search to find a child&apos;s records across
              all LEAs.
            </p>
          </div>
        </Card>
      ) : isLoading ? (
        <Card className="card-custom shadow-none">
          <div className="d-flex align-items-center justify-content-center py-5 gap-3">
            <div
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
              style={{ color: "#0d2d4a" }}
            />
            <span style={{ color: "#0d2d4a" }}>Searching...</span>
          </div>
        </Card>
      ) : showNoResults ? (
        <Card className="card-custom shadow-none">
          <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
            <h5 className="fw-semibold mb-2" style={{ color: "#0d2d4a" }}>
              No Matching Children Found
            </h5>
            <p
              className="mb-0"
              style={{ maxWidth: 380, fontSize: "0.9rem", color: "#212529" }}
            >
              Try adjusting your search criteria and searching again.
            </p>
          </div>
        </Card>
      ) : null}

      {showPopup && (
        <Popup
          show={showPopup}
          header={`${popupResults.length} Matching Children Found`}
          onClose={() => setShowPopup(false)}
          hasButton={false}
          backButtonText="Exit"
          onBackButtonClick={() => setShowPopup(false)}
          size="lg"
        >
          <KendoTableClientState
            data={popupResults}
            columns={columns}
            onRowClick={handleRowClick}
            filterable={false}
            noItemsMessage="No matching children found"
            initialTake={popupResults.length || 10}
          />
        </Popup>
      )}

      {selectedCsppid && (
        <Suspense
          fallback={
            <Card className="card-custom shadow-none">
              <div className="d-flex align-items-center justify-content-center py-5 gap-3">
                <div
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                  style={{ color: "#0d2d4a" }}
                />
                <span style={{ color: "#0d2d4a" }}>
                  Loading child records...
                </span>
              </div>
            </Card>
          }
        >
          {/* key forces a fresh instance per child, so accordion/export state
              from a previous search doesn't leak into the next one. */}
          <ChildDataDetail key={selectedCsppid} csppid={selectedCsppid} />
        </Suspense>
      )}
    </>
  );
}
